import React, { useEffect, useState } from "react";
import YouTubeShortsPost from "./YouTubeShortPreview";
import YouTubeVideoPost from "./YoutubeVideoPreview";
import { Post } from "../../../types/posts";
import Spinner from "../../misc/Spinner";

export function getRelativeTime(post: Post): string {
  const date = post?.postTime
    ? new Date(post.postTime)
    : post?.scheduledFor
    ? new Date(post.scheduledFor)
    : null;

  if (!date) return "";

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(Math.abs(diffMs) / (1000 * 60));

  if (diffMs >= 0) {
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr`;
    const diffDays = Math.floor(diffHr / 24);
    return `${diffDays} days`;
  } else {
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr`;
    const diffDays = Math.floor(diffMin / 1440);
    return `${diffDays} days`;
  }
}

function isShortFromAspectRatio(aspectRatio: string): boolean {
  const parts = aspectRatio.split(":");
  if (parts.length !== 2) return false;
  const w = parseFloat(parts[0]);
  const h = parseFloat(parts[1]);
  if (!w || !h) return false;
  return w / h <= 0.8;
}

// Persists for the JS context lifetime (cleared on page refresh). Keeps the
// detected Short/Video shape stable across processing-complete transitions so
// the preview doesn't briefly flash back to a spinner while the video probe
// re-detects what we already know from the thumbnail.
const shortDetectionCache = new Map<string, boolean>();

const YouTubePost = ({ post, onShortDetected }: { post: Post; onShortDetected?: (isShort: boolean) => void; initialIsShort?: boolean | null }) => {
  const videoUrl = post?.media?.[0]?.signedUrl;
  const isProcessing = post?.media?.[0]?.isProcessing ?? false;
  const aspectRatio = (post?.media?.[0] as any)?.videoMetadata?.aspectRatio as string | undefined;
  const thumbnail = post?.media?.[0]?.thumbnail;
  const mediaId = post?.media?.[0]?.id;

  const metadataIsShort = aspectRatio != null ? isShortFromAspectRatio(aspectRatio) : null;
  const cachedIsShort = mediaId ? shortDetectionCache.get(mediaId) : undefined;

  // committed = what we actually render; null means detection is in progress.
  // We never pre-seed from initialIsShort: Radix Tabs unmounts inactive tab content,
  // so on remount initialIsShort could be stale (e.g. landscape hint after user swapped
  // to a vertical video while on a different tab). Always derive from metadata, cache,
  // or async detection.
  const [committed, setCommitted] = useState<boolean | null>(
    cachedIsShort !== undefined ? cachedIsShort : metadataIsShort,
  );

  useEffect(() => {
    const remember = (short: boolean): void => {
      if (mediaId) shortDetectionCache.set(mediaId, short);
      setCommitted(short);
      onShortDetected?.(short);
    };

    // Backend-provided metadata is authoritative — always wins.
    if (aspectRatio != null) {
      remember(isShortFromAspectRatio(aspectRatio));
      return;
    }

    // Cache hit — reuse what we already detected earlier in this session so
    // the preview doesn't flash back to a spinner when processing completes
    // and the video URL becomes available.
    if (mediaId && shortDetectionCache.has(mediaId)) {
      const short = shortDetectionCache.get(mediaId) as boolean;
      setCommitted(short);
      onShortDetected?.(short);
      return;
    }

    // While the video is processing (e.g. ZapCap captioning) the video file isn't
    // playable yet, but the thumbnail usually has the same aspect ratio — use it
    // to pick the correct Short vs Video shape so the processing overlay renders
    // in the right frame.
    if (isProcessing) {
      if (!thumbnail) {
        setCommitted(false);
        return;
      }
      setCommitted(null);
      let cancelled = false;
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          remember(img.naturalWidth / img.naturalHeight <= 0.8);
        } else {
          setCommitted(false);
        }
      };
      img.onerror = () => {
        if (cancelled) return;
        setCommitted(false);
      };
      img.src = thumbnail;
      return () => {
        cancelled = true;
      };
    }

    if (!videoUrl) {
      setCommitted(null);
      return;
    }

    // Reset to null so only the loader shows until detection completes,
    // preventing a layout shift where the wrong component renders with the new video.
    setCommitted(null);

    const video = document.createElement("video");
    video.src = videoUrl;
    video.preload = "metadata";

    let resolved = false;

    const finalize = (short: boolean, cache: boolean): void => {
      if (resolved) return;
      resolved = true;
      if (cache) {
        remember(short);
      } else {
        setCommitted(short);
        onShortDetected?.(short);
      }
    };

    video.onloadedmetadata = () => {
      const ratio = video.videoWidth / video.videoHeight;
      finalize(ratio <= 0.8, true);
    };

    video.onerror = () => {
      // Don't cache an error fallback — let a later attempt re-probe.
      finalize(false, false);
    };

    // Fallback for mobile (iOS Safari) where neither event fires
    const fallbackTimer = setTimeout(() => {
      finalize(false, false);
    }, 4000);

    return () => {
      resolved = true;
      clearTimeout(fallbackTimer);
      video.src = "";
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, aspectRatio, isProcessing, thumbnail, mediaId]);

  // While ZapCap is still processing and we don't yet know orientation, render
  // the Video-shaped preview so its built-in "Video Processing" overlay is
  // visible immediately (matching LinkedIn/Facebook/etc.) instead of a bare
  // spinner. Once detection resolves, the correct shape takes over.
  if (committed === null && isProcessing) {
    return <YouTubeVideoPost key="processing" post={post} />;
  }

  // While detecting video type, show a spinner so the preview area doesn't
  // collapse to blank when the user swaps to a new video.
  if (committed === null) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Spinner />
      </div>
    );
  }

  if (committed) {
    return <YouTubeShortsPost key="short" post={post} />;
  }

  return <YouTubeVideoPost key="video" post={post} />;
};

export default YouTubePost;
